import React, { useMemo } from 'react';
import { clsx } from 'clsx';
import { XMarkIcon, AdjustmentsHorizontalIcon } from '@/components/icons';

import type { FilterChipsProps, ActiveFilter } from '@/types/filters';

/**
 * Filter Chips Component
 * 
 * Displays active filters as removable chips:
 * - Individual filter removal
 * - Clear all functionality
 * - Overflow handling with "show more" option
 * - Visual feedback with colors and icons
 */
export function FilterChips({
  activeFilters,
  onRemoveFilter,
  onClearAll,
  className,
  maxVisible = 5,
  showClearAll = true,
  renderChip
}: FilterChipsProps) {

  // Sort and group filters for display
  const { visibleFilters, hiddenCount } = useMemo(() => {
    if (activeFilters.length <= maxVisible) {
      return {
        visibleFilters: activeFilters,
        hiddenCount: 0
      };
    }
    
    // Prioritize certain filter types to show first
    const priorityOrder = ['search', 'status', 'priority', 'assignment'];
    
    const sorted = [...activeFilters].sort((a, b) => {
      const aIndex = priorityOrder.findIndex(type => a.filterId.includes(type));
      const bIndex = priorityOrder.findIndex(type => b.filterId.includes(type));
      
      if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
      if (aIndex !== -1) return -1;
      if (bIndex !== -1) return 1;
      return 0;
    });
    
    return {
      visibleFilters: sorted.slice(0, maxVisible),
      hiddenCount: sorted.length - maxVisible
    };
  }, [activeFilters, maxVisible]);

  // Default chip renderer
  const defaultRenderChip = (filter: ActiveFilter) => (
    <div
      key={filter.id}
      className={clsx(
        'inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium',
        'bg-gray-100 text-gray-800 border border-gray-200',
        'hover:bg-gray-200 transition-colors',
        filter.color && 'border-l-4'
      )}
      style={filter.color ? { borderLeftColor: filter.color } : undefined}
    >
      {/* Filter Label and Value */}
      <span className="max-w-32 truncate">
        <span className="text-gray-600">{filter.label}:</span>{' '}
        <span className="font-semibold">{filter.displayValue}</span>
      </span>
      
      {/* Remove Button */}
      {filter.removable && (
        <button
          onClick={() => onRemoveFilter(filter.id)}
          className="ml-1 p-0.5 rounded-full hover:bg-gray-300 text-gray-500 hover:text-gray-700"
          title={`Remove ${filter.label} filter`}
          aria-label={`Remove ${filter.label}: ${filter.displayValue} filter`}
        >
          <XMarkIcon className="w-3 h-3" />
        </button>
      )}
    </div>
  );

  // Priority-based chip colors
  const getPriorityChipStyle = (filter: ActiveFilter) => {
    if (filter.filterId === 'priorities') {
      const priorityColors: Record<string, string> = {
        Low: 'bg-green-100 text-green-800 border-green-200',
        Medium: 'bg-yellow-100 text-yellow-800 border-yellow-200',
        High: 'bg-orange-100 text-orange-800 border-orange-200', 
        Urgent: 'bg-red-100 text-red-800 border-red-200',
        Critical: 'bg-red-900 text-red-100 border-red-800'
      };
      return priorityColors[filter.value] || 'bg-gray-100 text-gray-800 border-gray-200';
    }
    
    if (filter.filterId === 'statuses') {
      const statusColors: Record<string, string> = {
        New: 'bg-slate-100 text-slate-800 border-slate-200',
        Open: 'bg-green-100 text-green-800 border-green-200',
        Pending: 'bg-blue-100 text-blue-800 border-blue-200',
        OnHold: 'bg-yellow-100 text-yellow-800 border-yellow-200',
        Resolved: 'bg-purple-100 text-purple-800 border-purple-200',
        Closed: 'bg-gray-100 text-gray-800 border-gray-200',
        Cancelled: 'bg-red-100 text-red-800 border-red-200'
      };
      return statusColors[filter.value] || 'bg-gray-100 text-gray-800 border-gray-200';
    }
    
    return 'bg-gray-100 text-gray-800 border-gray-200';
  };

  // Enhanced chip renderer with better styling
  const enhancedRenderChip = (filter: ActiveFilter) => (
    <div
      key={filter.id}
      className={clsx(
        'inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium',
        'transition-all duration-200 hover:shadow-sm',
        getPriorityChipStyle(filter)
      )}
    >
      {/* Filter Label and Value */}
      <span className="max-w-36 truncate">
        <span className="opacity-75">{filter.label}:</span>{' '}
        <span className="font-semibold">{filter.displayValue}</span>
      </span>
      
      {/* Color Indicator */}
      {filter.color && !filter.filterId.includes('status') && !filter.filterId.includes('priority') && (
        <div 
          className="w-2 h-2 rounded-full flex-shrink-0"
          style={{ backgroundColor: filter.color }}
          aria-hidden="true"
        />
      )}
      
      {/* Remove Button */}
      {filter.removable && (
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onRemoveFilter(filter.id);
          }}
          className="ml-1 p-0.5 rounded-full hover:bg-black hover:bg-opacity-10 opacity-60 hover:opacity-100 transition-all"
          title={`Remove ${filter.label} filter`}
          aria-label={`Remove ${filter.label}: ${filter.displayValue} filter`}
        >
          <XMarkIcon className="w-3 h-3" />
        </button>
      )}
    </div>
  );

  // No active filters
  if (activeFilters.length === 0) {
    return null;
  }

  return (
    <div className={clsx('space-y-3', className)}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center text-sm text-gray-600">
          <AdjustmentsHorizontalIcon className="w-4 h-4 mr-2" />
          <span>
            {activeFilters.length} active filter{activeFilters.length !== 1 ? 's' : ''}
          </span>
        </div>
        
        {showClearAll && activeFilters.length > 0 && (
          <button
            onClick={onClearAll}
            className="text-xs text-gray-500 hover:text-gray-700 underline"
          >
            Clear all
          </button>
        )}
      </div>
      
      {/* Filter Chips */}
      <div className="flex flex-wrap gap-2">
        {visibleFilters.map(filter => 
          renderChip ? renderChip(filter) : enhancedRenderChip(filter)
        )}
        
        {/* Show more indicator */}
        {hiddenCount > 0 && (
          <div className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600 border border-gray-200">
            +{hiddenCount} more
          </div>
        )}
      </div>
      
      {/* Divider */}
      <div className="border-t border-gray-200" />
    </div>
  );
}